import type { FitRecordedMetadata } from '../utils/fitRecordedMetrics';
export interface WorkoutAnnualRecord { peak_type: string; value: number; previous_value: number; improvement_percentage: number; year: number; }

import { retryWorkoutStream } from '../utils/retryWorkoutStream';
import type { RunningMetrics } from '../utils/runningWorkoutMetrics';
import type { RunningProfile, RunningPeriod } from './runningProfile';
export type { RunningProfile, RunningEffort, RunningBenchmark, RunningProfilePoint, RunningPeriod } from './runningProfile';
import type { AxiosInstance, AxiosProgressEvent } from 'axios';
import axios from 'axios';

const DEFAULT_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
  'https://api.vo2peaks.com';

let requestLocale = 'fa';
let savedPreference = '';
let instance: AxiosInstance = createInstance(DEFAULT_BASE_URL);

function createInstance(baseURL: string): AxiosInstance {
  return axios.create({
    baseURL,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'Accept-Language': requestLocale },
  });
}

export function configureApi(baseURL: string, token?: string): void {
  instance = createInstance(baseURL);
  if (token) {
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    syncLocalePreference();
  }
}

export function setAuthToken(token: string): void {
  instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    syncLocalePreference();
}

export function setApiLocale(locale: string): void {
  requestLocale = locale;
  instance.defaults.headers.common['Accept-Language'] = locale;
  syncLocalePreference();
}

function syncLocalePreference(): void {
  const auth = instance.defaults.headers.common['Authorization'];
  if (!auth) return;
  const identity = `${auth}:${requestLocale}`;
  if (savedPreference === identity) return;
  savedPreference = identity;
  void instance.put('/api/v1/mobile/account/locale', { locale: requestLocale }).catch(() => {
    if (savedPreference === identity) savedPreference = '';
  });
}

export function clearAuthToken(): void {
  savedPreference = '';
  delete instance.defaults.headers.common['Authorization'];
}

/** Expose the shared, configured axios instance for sub-services (e.g. atpApi). */
export function getApiInstance(): AxiosInstance {
  return instance;
}

// Preserve an invitation across the OTP/profile steps; never infer a coach assignment.
const incomingReferral = typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('ref');
if (incomingReferral && /^[A-Za-z0-9]{1,24}$/.test(incomingReferral)) localStorage.setItem('vo2.pending-referral', incomingReferral);
function rememberRegistrationProof(mobile: string, body: unknown): void {
  const token = (body as { data?: { registration_token?: string } })?.data?.registration_token;
  if (token) sessionStorage.setItem(`vo2.registration-proof.${mobile}`, token);
}

// ─── Auth Endpoints ──────────────────────────────────────────────────────────

export interface LoginPayload {
  mobile: string;
  password: string;
}

export interface OtpPayload {
  mobile: string;
  otp: string;
}

// Raw shape returned by /login-with-password and /login-by-otp
// {
//   status: "success",
//   message: "...",
//   data: {
//     token: string,
//     data: {
//       user: { ... }
//     }
//   }
// }
export interface AuthResponse {
  data: {
    token: string;
    data: {
      user: ApiUser;
    };
  };
}

// User object as returned by the API
export interface ApiUser {
  id: number;
  full_name: string;
  profile_image?: string;
  is_coached?: boolean;
  is_register_completed?: boolean;
  is_mobile_verified?: boolean;
  account_expire_date?: string;
  is_subscription_active?: boolean;
  days_remaining?: number;
  role?: string;
  now?: string;
  mobile?: string;
  email?: string;
  athletes?: AthleteItem[];
}

// Normalized shape used across the desktop app
export interface UserProfile {
  id: number;
  name: string;
  avatar?: string;
  mobile?: string;
  email?: string;
  // Keep additional fields from the backend for future use
  full_name?: string;
  profile_image?: string;
  is_coached?: boolean;
  is_register_completed?: boolean;
  is_mobile_verified?: boolean;
  account_expire_date?: string;
  is_subscription_active?: boolean;
  days_remaining?: number;
  role?: string;
  now?: string;
  athletes?: AthleteItem[];
}

export function mapApiUserToProfile(user: ApiUser): UserProfile {
  return {
    id: user.id,
    name: user.full_name ?? '',
    avatar: user.profile_image,
    mobile: user.mobile,
    email: user.email,
    full_name: user.full_name,
    profile_image: user.profile_image,
    is_coached: user.is_coached,
    is_register_completed: user.is_register_completed,
    is_mobile_verified: user.is_mobile_verified,
    account_expire_date: user.account_expire_date,
    is_subscription_active: user.is_subscription_active,
    days_remaining: user.days_remaining,
    role: user.role,
    now: user.now,
    athletes: user.athletes ?? [],
  };
}

export interface CheckMobileResponse {
  is_user_exists: boolean;
  is_register_completed: boolean;
}

export interface CompleteRegisterPayload {
  first_name: string;
  last_name: string;
  gender: string;
  role_id: string;
  sport_type_id?: string;
  weight?: string;
  age?: string;
  lthr?: string;
  ftp?: string;
}

export interface RegisterPayload extends CompleteRegisterPayload {
  mobile: string;
  password: string;
}

export const auth = {
  /** POST /api/v1/check-mobile */
  checkMobile: (mobile: string) =>
    instance.post<{ data: CheckMobileResponse }>('/api/v1/check-mobile', { mobile }),

  /** POST /api/v1/login */
  login: (payload: LoginPayload) => instance.post<AuthResponse>('/api/v1/login', payload),

  /** POST /api/v1/login-with-password */
  loginWithPassword: (payload: LoginPayload) =>
    instance.post<AuthResponse>('/api/v1/login-with-password', payload),

  /** POST /api/v1/send-otp */
  sendOtp: (mobile: string) => instance.post('/api/v1/send-otp', { mobile }),

  /** POST /api/v1/login-by-otp */
  loginByOtp: async (payload: OtpPayload) => {
    const res = await instance.post<AuthResponse>('/api/v1/login-by-otp', payload);
    rememberRegistrationProof(payload.mobile, res.data);
    return res;
  },

  /** GET /api/v1/me */
  me: () => instance.get<{ data: UserProfile }>('/api/v1/me'),

  /** POST /api/v1/logout */
  logout: () => instance.post('/api/v1/logout'),

  /** POST /api/v1/refresh-token */
  refreshToken: () => instance.post<AuthResponse>('/api/v1/refresh-token'),

  /** POST /api/v1/register — new user full registration */
  register: async (payload: RegisterPayload) => {
    const proof = sessionStorage.getItem(`vo2.registration-proof.${payload.mobile}`);
    const referral = localStorage.getItem('vo2.pending-referral');
    const res = await instance.post<AuthResponse>('/api/v1/register', {
      ...payload, ...(proof ? { registration_token: proof } : {}),
    });
    sessionStorage.removeItem(`vo2.registration-proof.${payload.mobile}`);
    if (proof && referral) {
      try {
        await instance.post('/api/v1/mobile/referrals/claim', { code: referral }, {
          headers: { Authorization: `Bearer ${res.data.data.token}` },
        });
        localStorage.removeItem('vo2.pending-referral');
      } catch {
        // Keep the code editable in the referral panel; signup already succeeded.
      }
    }
    return res;
  },

  /** POST /api/v1/completeRegister — finish profile for existing token */
  completeRegister: (payload: CompleteRegisterPayload) =>
    instance.post<{ data: { user: ApiUser } }>('/api/v1/completeRegister', payload),
};

// ─── Workout Endpoints ───────────────────────────────────────────────────────

/** Payload inside ApiResponse.success for GET synced-fit-files */
export interface SyncedFitFilesPayload {
  files: string[];
  items?: WorkoutImportItem[];
  imports?: WorkoutImportItem[];
  total?: number;
  user_id?: number;
}

/** Single import row returned by upload-fit-file / upload-wse-file / synced-fit-files */
export interface WorkoutImportItem {
  id?: number | null;
  filename?: string;
  normalized_filename?: string;
  file_size?: number;
  start_time?: number | null;
  status?: string;
  workout_id?: number | null;
  source?: string;
  error_message?: string | null;
}

export interface WorkoutUploadResult {
  queued?: string[];
  queued_count?: number;
  processed?: string[];
  processed_count?: number;
  failed?: Array<{ filename: string; error: string }>;
  failed_count?: number;
  imports?: WorkoutImportItem[];
}

export interface WorkoutType {
  id: number;
  name: string;
  english_name: string;
  icon?: string;
}

export type TrainingLoadMethod = 'PWR' | 'HR' | 'PACE' | 'CSS' | 'RPE';
export interface TrainingLoadMeta {
  value: number | null;
  method: TrainingLoadMethod | null;
  coverage: number | null;
  raw: number | null;
  details?: Record<string, string | number | boolean | null> | null;
}

export interface Workout {
  id: number;
  title: string;
  date: string;
  sport_type?: string;
  duration?: number;
  distance?: number;
  tss?: number;
  status?: string;
  created_at: string;
}

/** Pushable workout returned by the dedicated endpoint (filtered for structure + today+). */
export interface PushableWorkout {
  id: number;
  title: string;
  date: string;
  sport_type: string;
  duration: number | null;
  tss: number | null;
  created_at: string;
  has_structure: boolean;
}

export interface WorkoutDetail {
  annual_records?: WorkoutAnnualRecord[];
  annual_records_count?: number;
  id: number;
  title: string;
  workout_day: string;
  workout_type: { id: number; name: string; english_name: string; icon?: string };
  total_time: number | null;
  total_time_planned: string | null;
  tss_actual: number | null;
  training_load?: TrainingLoadMeta | null;
  training_load_method?: string | null;
  power_quality?: WorkoutPowerQuality;
  running_metrics?: RunningMetrics | null;
  strava?: { activity_id: string; url: string; device_name?: string | null } | null;
  source?: string | null;
  fit_sport?: number | null;
  tss_planned: string | null;
  if_actual: string | number | null;
  if_planned: string | null;
  distance: number | null;
  distance_planned: string | null;
  calories: string | null;
  calories_planned?: string | null;
  description: string | null;
  feeling?: string | number | null;
  rpe?: string | number | null;
  has_details: boolean;
  fit_file: string | boolean | null;
  garmin_schedule_id?: string | null;
  // Extra fields returned by the API
  work?: number | null;
  np?: number | null;
  vi?: number | string | null;
  ef?: number | string | null;
  pwhr?: number | string | null;
  wkg?: number | string | null;
  vam?: number | string | null;
  grade?: number | string | null;
  total_ascent?: number | null;
  total_descent?: number | null;
  left_balance?: string | null;
  structure: {
    structure: any[];
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
  } | null;
  comments: Array<{
    id: number;
    description: string;
    comment_type?: number;
    created_at: string;
    user: { full_name: string; profile_image?: string };
  }>;
  heart_rate_minimum?: number | null;
  heart_rate_maximum?: number | null;
  heart_rate_average?: number | null;
  power_average?: number | null;
  max_power?: number | null;
  avg_cadence?: number | null;
  max_cadence?: number | null;
  avg_speed?: number | null;
  max_speed?: number | null;
  files: any[];
  details: {
    zones: { hr_zones: any[]; pw_zones: any[] };
    peaks: { sd_peak?: any; hr_peak?: any; pw_peak?: any; cd_peak?: any };
    lap_data: any[];
    fit_metrics?: FitRecordedMetadata | null;
    min_max_avg: {
      altitude?: { min?: number | null; avg?: number | null; max?: number | null };
      temperature?: { min?: number | null; avg?: number | null; max?: number | null };
      heart_rate?: { min: number; avg: number; max: number };
      power?: { min: number; avg: number; max: number };
      cadence?: { min: number; avg: number; max: number };
      speed?: { min: number; avg: number; max: number };
      respiration_rate?: { min?: number | null; avg?: number | null; max?: number | null };
    };
    data?: { data: any[]; session?: any };
  } | null;
}

export interface CoachAthlete {
  id: number;
  full_name: string;
  first_name?: string;
  last_name?: string;
  profile_image?: string;
  mobile?: string;
  email?: string;
  coaching_relationship?: 'primary' | 'assistant';
  /** ISO timestamp of the athlete's latest uploaded or completed workout. */
  last_workout_uploaded_at?: string | null;
}

/** Normalized athlete shape used across the desktop app (for athlete switcher). */
export interface AthleteItem {
  id: number;
  full_name: string;
  profile_image?: string;
  coaching_relationship?: 'primary' | 'assistant';
  /** ISO timestamp — latest processed FIT import or completed FIT workout. */
  last_workout_uploaded_at?: string | null;
}

export const user = {
  /** GET /api/v1/mobile/user/get-athletes */
  getAthletes: () =>
    instance.get<{ data: { athletes: CoachAthlete[] } }>('/api/v1/mobile/user/get-athletes'),
};

export interface PlannedMetricsPreview {
  total_time_planned?: number;
  distance_planned?: number;
  if_planned?: number;
  tss_planned?: number;
}

export interface GenerateWorkoutFromPromptPayload {
  prompt: string;
  context_scope?: 'athlete' | 'library';
  user_id?: number | null;
  workout_type_id?: number | null;
  locale?: string;
  conversation_id?: string | null;
  current_structure?: {
    structure: unknown[];
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
  } | null;
  workout_settings?: {
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
  };
}

export interface WorkoutAiChatMessage {
  role: 'user' | 'assistant' | string;
  content: string;
}

export interface GenerateWorkoutFromPromptResult {
  structure: {
    structure: unknown[];
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
  };
  suggested_title: string;
  suggested_workout_type?: { id: number; title: string; persian_title?: string | null; sport_type?: string | null } | null;
  planned_metrics: PlannedMetricsPreview;
  warnings: string[];
  provider: string;
  model: string;
  conversation_id?: string;
  assistant_message?: string;
  messages?: WorkoutAiChatMessage[];
}

export interface TranscribeWorkoutPromptAudioResult {
  transcript: string;
  provider: string;
  model: string;
  egress_mode?: string;
}

export interface WorkoutFtpRecommendation {
  status: 'ready' | 'applied' | 'unavailable';
  reason?: string | null;
  workout_id?: number;
  intensity_factor?: number | null;
  current_ftp_watts?: number | null;
  previous_ftp_watts?: number | null;
  proposed_ftp_watts?: number | null;
  delta_watts?: number | null;
  confidence?: { score: number; level: 'low' | 'medium' | 'high' };
  method?: string;
  evidence?: Record<string, unknown> | null;
  power_zones?: Record<string, unknown>;
  applied: boolean;
  applied_at?: string;
}

export interface WorkoutPowerQuality { status: 'eligible'|'quarantined'|'rejected'|'approved'; quarantined: boolean; reason: string|null; version: number; review_requested: boolean; review_note?: string|null; restoration_pending?: boolean; }
export const workouts = {
  getPowerQuality: (workoutId: number) => instance.get<{data: WorkoutPowerQuality}>(`/api/v1/mobile/workout/${workoutId}/power-quality`),
  requestPowerReview: (workoutId: number, version: number, note: string) => instance.post<{data: WorkoutPowerQuality}>(`/api/v1/mobile/workout/${workoutId}/power-quality/review-request`, {version,note}),
  /** POST /api/v1/mobile/workout/generate-from-prompt — LLM structured workout preview */
  generateFromPrompt: (payload: GenerateWorkoutFromPromptPayload) =>
    instance.post<{ data: GenerateWorkoutFromPromptResult }>(
      '/api/v1/mobile/workout/generate-from-prompt',
      payload,
      { timeout: 90_000 },
    ),

  /** POST /api/v1/mobile/workout/transcribe-prompt-audio — voice → text for Workout AI prompt */
  transcribePromptAudio: (formData: FormData) =>
    instance.post<{ data: TranscribeWorkoutPromptAudioResult }>(
      '/api/v1/mobile/workout/transcribe-prompt-audio',
      formData,
      {
        timeout: 60_000,
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    ),

  /** GET /api/v1/mobile/workout/synced-fit-files */
  getSyncedFitFiles: (userId: number, since?: number) =>
    instance.get<{ data: SyncedFitFilesPayload }>('/api/v1/mobile/workout/synced-fit-files', {
      params: {
        user_id: userId,
        ...(typeof since === 'number' ? { since } : {}),
      },
    }),

  /** POST /api/v1/mobile/workout/upload-fit-file — multipart/form-data, up to 20 files */
  uploadFitFiles: (
    files: Array<{ name: string; data: Blob }>,
    userId: number,
    onProgress?: (progress: number) => void
  ) => {
    const form = new FormData();
    form.append('user_id', String(userId));
    // Backend expects "fit_file" (Laravel: fit_file, fit_file.*, required|file|max:20480)
    files.forEach((f) => form.append('fit_file[]', f.data, f.name));

    return instance.post<{ data: WorkoutUploadResult }>(
      '/api/v1/mobile/workout/upload-fit-file',
      form,
      {
        // Let the browser set multipart boundary (do not force Content-Type)
        headers: { 'Content-Type': undefined },
        timeout: 300_000,
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (e.total && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      }
    );
  },

  /** POST /api/v1/mobile/workout/upload-wse-file — multipart/form-data, up to 20 files */
  uploadWseFiles: (
    files: Array<{ name: string; data: Blob }>,
    userId: number,
    onProgress?: (progress: number) => void
  ) => {
    const form = new FormData();
    form.append('user_id', String(userId));
    files.forEach((f) => form.append('wse_file[]', f.data, f.name));

    return instance.post<{ data: WorkoutUploadResult }>(
      '/api/v1/mobile/workout/upload-wse-file',
      form,
      {
        headers: { 'Content-Type': undefined },
        timeout: 300_000,
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (e.total && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      }
    );
  },

  /** POST /api/v1/mobile/workout/upload-tcx-file — TCX is converted to canonical FIT server-side */
  uploadTcxFiles: (
    files: Array<{ name: string; data: Blob }>,
    userId: number,
    onProgress?: (progress: number) => void
  ) => {
    const form = new FormData();
    form.append('user_id', String(userId));
    files.forEach((f) => form.append('tcx_file[]', f.data, f.name));

    return instance.post<{ data: WorkoutUploadResult }>(
      '/api/v1/mobile/workout/upload-tcx-file',
      form,
      {
        headers: { 'Content-Type': undefined },
        timeout: 300_000,
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (e.total && onProgress) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      }
    );
  },

  /** Shared workout/event sport source with an offline-safe static fallback. */
  getWorkoutTypes: () =>
    instance
      .get<{ data: { workoutTypes: WorkoutType[] } }>('/api/v1/mobile/workout/workout-types')
      .catch(() =>
        import('../data/staticSportData').then(({ STATIC_WORKOUT_TYPES }) =>
          ({ data: { data: { workoutTypes: STATIC_WORKOUT_TYPES } } } as {
            data: { data: { workoutTypes: WorkoutType[] } };
          }),
        ),
      ),

  /** GET /api/v1/mobile/workout/get-pushable-workouts — structured workouts from today onward */
  getPushableWorkouts: (userId?: number | null) =>
    instance.get<{ data: { workouts: PushableWorkout[] } }>(
      '/api/v1/mobile/workout/get-pushable-workouts',
      { params: userId ? { user_id: userId } : undefined }
    ),

  /** GET /api/v1/mobile/workout/get-workout-by-date */
  getWorkoutsByDateRange: (startDate: string, endDate: string) =>
    instance.get<{ data: Workout[] }>('/api/v1/mobile/workout/get-workout-by-date', {
      params: { from: startDate, to: endDate },
    }),

  /** Download the recorded activity, or the structured plan when requested. */
  downloadFit: async (workoutId: number, structured = false): Promise<Blob> => {
    const response = await instance.get(
      `/api/v1/mobile/workout/${workoutId}/${structured ? 'download-structured-fit' : 'download-fit'}`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  /** GET /api/v1/mobile/workout/{id}/download-structured-fit */
  downloadStructuredFit: async (
    workoutId: number,
    options?: { sportType?: 'CYCLING' | 'RUNNING' },
  ): Promise<{ filename: string; base64: string }> => {
    const response = await instance.get(
      `/api/v1/mobile/workout/${workoutId}/download-structured-fit`,
      {
        params: options?.sportType ? { sport_type: options.sportType } : undefined,
        responseType: 'arraybuffer',
      },
    );

    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    const filenameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = filenameMatch
      ? filenameMatch[1].replace(/['"]/g, '')
      : `workout_${workoutId}.fit`;

    const bytes = new Uint8Array(response.data as ArrayBuffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    const base64 = btoa(binary);
    return { filename, base64 };
  },

  /** GET /api/v1/mobile/workout/{id} */
  getWorkout: (workoutId: number) =>
    instance.get<{ data: Workout }>(`/api/v1/mobile/workout/${workoutId}`),

  /** GET /api/v1/mobile/workout/{id} — full detail including zones, peaks, laps */
  getWorkoutDetail: (workoutId: number) =>
    instance.get<{ data: { workout: WorkoutDetail } }>(`/api/v1/mobile/workout/${workoutId}`),

  /** POST /api/v1/mobile/workout/add-comment */
  addComment: (payload: { workout_id: number; comment_type: number; description: string }) =>
    instance.post('/api/v1/mobile/workout/add-comment', payload),

  /** DELETE /api/v1/mobile/workout/comment/{commentId} */
  deleteComment: (commentId: number) =>
    instance.delete(`/api/v1/mobile/workout/comment/${commentId}`),

  /** PATCH /api/v1/mobile/workout/{id} — update workout fields (structure, title, planned metrics, …) */
  updateWorkout: (
    workoutId: number,
    workout: Record<string, unknown>,
    comments?: Array<Record<string, unknown>> | null,
  ) =>
    instance.patch(`/api/v1/mobile/workout/${workoutId}`, {
      data: { workout, ...(comments != null ? { comments } : {}) },
    }),

  getWorkoutDetails: (workoutId: number) =>
    instance.get<any>(`/api/v1/mobile/workout/${workoutId}/details`),

  /** GET /api/v1/mobile/workout/{id}/data — raw FIT stream (can be large; extended timeout) */
  getWorkoutData: (workoutId: number) =>
    retryWorkoutStream(() => instance.get<any>(`/api/v1/mobile/workout/${workoutId}/data`, { timeout: 120_000 })),

  getFtpRecommendation: (workoutId: number) =>
    instance.get<{ data: WorkoutFtpRecommendation }>(
      `/api/v1/mobile/workout/${workoutId}/ftp-recommendation`,
      { timeout: 120_000 },
    ),

  applyFtpRecommendation: (workoutId: number) =>
    instance.post<{ data: WorkoutFtpRecommendation }>(
      `/api/v1/mobile/workout/${workoutId}/ftp-recommendation/apply`,
      undefined,
      { timeout: 120_000 },
    ),

  /** GET /api/v1/mobile/workout/{id}/calculate-selected-range-data/user/{userId}?start_time=X&end_time=Y */
  calculateSelectedRange: (workoutId: number, userId: number, startTime: number, endTime: number) =>
    instance.get<any>(`/api/v1/mobile/workout/calculate-selected-range-data/${workoutId}/user/${userId}`, {
      params: { start_time: startTime, end_time: endTime },
    }),

  /** POST /api/v1/mobile/workout/{plannedId}/pair/{completedId} — merge completed into planned */
  pairWorkouts: (plannedId: number, completedId: number) =>
    instance.post(`/api/v1/mobile/workout/${plannedId}/pair/${completedId}`),

  /** DELETE /api/v1/mobile/workout/{workoutId}/pair — split actual into a new completed row */
  unpairWorkout: (workoutId: number) =>
    instance.delete(`/api/v1/mobile/workout/${workoutId}/pair`),
};

export interface PdcCurvePoint {
  key: string;
  interval: number;
  label: string;
  value: number;
  workout_id?: number;
}

export interface PowerDurationCurveResponse {
  start_date: string;
  end_date: string;
  peak_type: string;
  curve: PdcCurvePoint[];
}

export interface AdvancedPerformanceResponse {
  meta: { start_date: string; end_date: string; workout_count: number; selected_workout_id: number | null; weight_kg: number | null; ftp: number | null; generated_at: string };
  durability: {
    status: 'ready' | 'insufficient_data'; reason?: string | null; method?: string; duration_seconds: number;
    buckets: Array<{ kj_per_kg: number; power_watts: number | null; degradation_percent: number | null; workout_id: number | null; date: string | null; sample_size: number }>;
  };
  power_model: { status: 'ready' | 'insufficient_data'; reason?: string; cp_watts: number | null; mftp_watts?: number | null; w_prime_joules: number | null; w_prime_kj?: number | null; p_max_watts: number | null; r_squared?: number };
  estimated_ftp: {
    status: 'ready' | 'insufficient_data';
    reason?: string;
    method: 'vo2peaks_cp_wprime_evidence_v1';
    context: 'period_estimate_no_decay';
    configured_ftp_watts: number | null;
    min_duration_seconds: number;
    auto_applied: false;
    eftp_watts: number | null;
    mftp_watts: number | null;
    delta_from_configured_watts: number | null;
    delta_from_configured_percent: number | null;
    recommendation: 'set_initial_ftp' | 'review_increase' | 'review_decrease' | 'keep_configured' | 'insufficient_data';
    selected_effort: {
      interval_seconds: number; peak_power_watts: number; estimated_ftp_watts: number;
      workout_id: number; date: string;
    } | null;
    evidence: Array<{
      interval_seconds: number; peak_power_watts: number; estimated_ftp_watts: number;
      workout_id: number; date: string;
    }>;
    confidence: { score: number; level: 'low' | 'medium' | 'high'; reasons: string[] };
  };
  time_to_exhaustion: {
    status: 'ready' | 'insufficient_data';
    reason?: string;
    method: 'observed_mean_max_at_threshold_v1';
    threshold_watts: number | null;
    threshold_source: 'configured_ftp' | 'eftp' | 'mftp' | null;
    tte_seconds: number | null;
    auto_applied: false;
    evidence: {
      interval_seconds: number;
      peak_power_watts: number;
      workout_id: number;
      date: string;
    } | null;
  };
  w_prime_balance: {
    status: 'ready' | 'insufficient_data'; reason?: string; workout_id: number | null; cp_watts?: number; w_prime_kj?: number; minimum_balance_kj?: number;
    points: Array<{ seconds: number; power: number; balance_kj: number; balance_percent: number }>;
  };
  decoupling: {
    status: 'ready' | 'insufficient_data'; reason?: string | null; good_threshold_percent: number;
    points: Array<{ date: string; workout_id: number; title: string; decoupling_percent: number; efficiency_factor: number | null }>;
  };
  heatmap: {
    status: 'ready';
    days: Array<{ date: string; tss_actual: number; tss_planned: number; duration_seconds: number; planned_duration_seconds: number; workout_count: number; completed_count: number }>;
  };
  scatter: {
    status: 'ready' | 'insufficient_data'; reason?: string | null; workout_id: number | null; x_channel: string; y_channel: string; available_channels: string[];
    points: Array<{ x: number; y: number; seconds: number | null }>;
  };
  formula: { status: 'ready'; expression: string; allowed_metrics: string[]; points: Array<{ date: string; value: number }> };
}

export const analyze = {
  /** GET /api/v1/mobile/analyze/power-duration-curve/user/{userId} */
  getPowerDurationCurve: (
    userId: number,
    params: {
      start_date: string;
      end_date: string;
      peak_type?: 'pw_peak' | 'hr_peak' | 'sd_peak' | 'cd_peak';
      workout_type_id?: number;
      exclude_workout_id?: number;
    }
  ) =>
    instance.get<{ data: PowerDurationCurveResponse }>(
      `/api/v1/mobile/analyze/power-duration-curve/user/${userId}`,
      { params }
    ),
  getAdvancedPerformance: (
    userId: number,
    params: {
      start_date: string; end_date: string; workout_id?: number; durability_duration?: 60 | 300 | 1200;
      eftp_min_duration?: 180 | 300 | 600 | 1200 | 1800;
      scatter_x?: string; scatter_y?: string; formula?: string;
    },
  ) =>
    instance.get<{ data: AdvancedPerformanceResponse }>(
      `/api/v1/mobile/analyze/advanced-performance/user/${userId}`,
      { params, timeout: 120_000 },
    ),
};

// ─── Personal best records ───────────────────────────────────────────────────

export interface PersonalBestRecordItem {
  id: number;
  workout_id?: number;
  peak_type: string;
  value: string | number;
  interval: number;
  wkg?: string | number | null;
  record_date: string;
  previous_value?: string | number | null;
  improvement_percentage?: string | number | null;
  workout?: {
    id: number;
    title?: string | null;
    workout_day?: string | null;
    workout_type?: { name?: string } | null;
  } | null;
}

export type CoachDashboardStatus = 'at_risk' | 'monitor' | 'ready' | 'missing';

export interface CoachDashboardReason {
  code:
    | 'missing_data'
    | 'low_hrv'
    | 'poor_sleep'
    | 'high_fatigue'
    | 'high_soreness'
    | 'training_load'
    | 'ready'
    | 'low_readiness';
  value: number | null;
  score: number | null;
}

export interface CoachDashboardAthlete {
  id: number;
  full_name: string;
  profile_image?: string | null;
  coaching_relationship: 'primary' | 'assistant';
  status: CoachDashboardStatus;
  readiness: ReadinessToday | null;
  training_load: TodayTrends | null;
  today_workout: {
    id: number;
    title: string;
    sport?: string | null;
    completed: boolean;
    duration_planned?: number | null;
    tss_planned?: number | null;
    compliance_percent?: number | null;
  } | null;
  reason: CoachDashboardReason;
}

export interface CoachDashboardOverview {
  readiness_date: string;
  workout_date: string;
  counts: Record<CoachDashboardStatus, number>;
  athletes: CoachDashboardAthlete[];
}

export interface CoachPeakTrendsAthletePayload {
  athlete_id: number;
  athlete_name: string;
  profile_image?: string | null;
  records: PersonalBestRecordItem[];
}

export const coachDashboard = {
  /** One roster-wide snapshot for the coach triage dashboard. */
  getOverview: (date?: string) =>
    instance.get<{ data: CoachDashboardOverview }>('/api/v1/mobile/coach/dashboard/overview', {
      params: date ? { date } : undefined,
    }),

  /** One HTTP request for all accessible athletes' peak histories. */
  getPeakTrends: (params?: Record<string, string | number | boolean>) =>
    instance.get<{ data: { athletes: CoachPeakTrendsAthletePayload[] } }>(
      '/api/v1/mobile/coach/dashboard/peak-trends',
      { params },
    ),
};

export type DashboardAiScope = 'roster' | 'athlete';
export type DashboardAiSeverity = 'high' | 'medium' | 'positive';
export type DashboardAiActionType =
  | 'open_athlete'
  | 'open_performance'
  | 'open_charts'
  | 'open_calendar'
  | 'open_workout'
  | 'filter_missing'
  | 'open_roster';

export interface DashboardAiAction {
  type: DashboardAiActionType;
  athlete_id?: number | null;
  workout_id?: number;
  date?: string;
}

export interface DashboardAiEvidenceItem {
  key: string;
  value: number | null;
  unit: string;
}

export interface DashboardAiInsight {
  id: string;
  code: string;
  severity: DashboardAiSeverity;
  athlete_id: number | null;
  athlete_name: string | null;
  params: Record<string, string | number | null>;
  evidence: DashboardAiEvidenceItem[];
  action: DashboardAiAction;
}

export interface DashboardAiPerformanceSignal {
  code: string;
  tone: 'strength' | 'limiter';
  confidence: number;
  params: Record<string, string | number | null>;
  evidence: DashboardAiEvidenceItem[];
}

export interface DashboardAiPerformanceProfile {
  window_days: number;
  from: string;
  to: string;
  discipline: 'cycling' | 'running' | 'swimming' | 'multisport' | 'rowing' | 'snow' | 'other';
  workout_count: number;
  active_weeks: number;
  sensor_coverage_percent: number;
  strengths: DashboardAiPerformanceSignal[];
  limiters: DashboardAiPerformanceSignal[];
}

export interface DashboardAiInsights {
  scope: DashboardAiScope;
  athlete?: {
    id: number;
    name: string;
    sport?: {
      id: number | null;
      name: string | null;
      discipline: 'cycling' | 'running' | 'swimming' | 'multisport' | 'rowing' | 'snow' | 'other';
    };
  } | null;
  generated_at: string;
  confidence: number;
  insights: DashboardAiInsight[];
  performance_profile?: DashboardAiPerformanceProfile | null;
  evidence: Record<string, number | null>;
  suggested_prompts: string[];
}

export interface DashboardAiHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface DashboardAiMetricGridBlock {
  id: string;
  type: 'metric_grid';
  title_key: string;
  title_params?: Record<string, string | number | null>;
  items: Array<{
    key: string;
    value: number | string | null;
    unit?: string;
    tone?: 'accent' | 'positive' | 'warning' | 'neutral';
  }>;
}

export interface DashboardAiChartBlock {
  id: string;
  type: 'chart';
  chart_type: 'line' | 'bar';
  title_key: string;
  title_params?: Record<string, string | number | null>;
  x_key?: string;
  series: Array<{
    key: string;
    label_key: string;
    unit?: string;
    points: Array<{ x: string; y: number | null; workout_id?: number }>;
  }>;
}

export interface DashboardAiTableBlock {
  id: string;
  type: 'table';
  title_key: string;
  title_params?: Record<string, string | number | null>;
  columns: Array<{ key: string; label_key: string }>;
  rows: Array<Record<string, string | number | null | DashboardAiAction>>;
}

export interface DashboardAiWorkoutListBlock {
  id: string;
  type: 'workout_list';
  title_key: string;
  title_params?: Record<string, string | number | null>;
  items: Array<{
    workout_id: number;
    date: string;
    title: string;
    sport?: string;
    duration_seconds?: number | null;
    tss?: number | null;
    metric_key?: string;
    metric_value?: number | null;
    metric_unit?: string;
    action?: DashboardAiAction;
  }>;
}

export type DashboardAiUiBlock =
  | DashboardAiMetricGridBlock
  | DashboardAiChartBlock
  | DashboardAiTableBlock
  | DashboardAiWorkoutListBlock;

export interface DashboardAiSource {
  workout_id: number;
  date: string;
  title: string;
  action?: DashboardAiAction;
}

export interface DashboardAiAnalysis {
  answer: string;
  source: 'llm' | 'deterministic';
  model?: string | null;
  generated_at: string;
  confidence: number;
  scope: DashboardAiScope;
  athlete?: DashboardAiInsights['athlete'];
  evidence: Record<string, number | null>;
  actions: DashboardAiAction[];
  blocks?: DashboardAiUiBlock[];
  sources?: DashboardAiSource[];
  tool_trace?: Array<{ name: string; status: 'ready' | 'insufficient_data' | 'error' | string }>;
}

export const dashboardAi = {
  getInsights: (userId?: number | null) =>
    instance.get<{ data: DashboardAiInsights }>('/api/v1/mobile/ai/dashboard/insights', {
      params: userId ? { user_id: userId } : undefined,
    }),

  analyze: (payload: {
    message: string;
    user_id?: number;
    locale?: 'en' | 'fa' | 'ar' | 'tr';
    history?: DashboardAiHistoryMessage[];
  }) =>
    instance.post<{ data: DashboardAiAnalysis }>('/api/v1/mobile/ai/dashboard/analyze', payload),

  transcribeAudio: (formData: FormData) =>
    instance.post<{ data: { transcript: string; language?: string } }>(
      '/api/v1/mobile/workout/transcribe-prompt-audio',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
};

export interface AthleteComparisonSubject {
  id: number;
  name: string;
  sport: {
    id?: number | null;
    name?: string | null;
    discipline: 'cycling' | 'running' | 'swimming' | 'multisport' | 'rowing' | 'snow' | 'other';
  };
  confidence: number;
  evidence: Record<string, number | null>;
  period: Record<string, number | string | null>;
  previous_period: Record<string, number | string | null>;
}

export interface AthleteComparisonMetric {
  key: string;
  label_key: string;
  unit: string;
  athlete_a: number | null;
  athlete_b: number | null;
  delta: number | null;
}

export interface AthleteComparisonOverview {
  generated_at: string;
  period: { from: string; to: string; days: number };
  same_discipline: boolean;
  confidence: number;
  athletes: [AthleteComparisonSubject, AthleteComparisonSubject];
  metrics: AthleteComparisonMetric[];
  insights: Array<{
    code: string;
    severity: 'high' | 'medium' | 'positive';
    winner?: 'athlete_a' | 'athlete_b';
    delta?: number;
  }>;
  suggested_prompts: string[];
  blocks: DashboardAiUiBlock[];
}

export interface AthleteComparisonAnalysis extends AthleteComparisonOverview {
  answer: string;
  source: 'llm' | 'deterministic';
  model?: string | null;
}

export interface AthleteComparisonPayload {
  athlete_a_id: number;
  athlete_b_id: number;
  from: string;
  to: string;
}

export const athleteComparisonAi = {
  overview: (payload: AthleteComparisonPayload) =>
    instance.post<{ data: AthleteComparisonOverview }>('/api/v1/mobile/ai/comparison/overview', payload),

  analyze: (
    payload: AthleteComparisonPayload & {
      message: string;
      locale?: 'en' | 'fa' | 'ar' | 'tr';
      history?: DashboardAiHistoryMessage[];
    },
  ) =>
    instance.post<{ data: AthleteComparisonAnalysis }>(
      '/api/v1/mobile/ai/comparison/analyze',
      payload,
      { timeout: 90_000 },
    ),
};

export const records = {
  getHistory: (
    userId: number,
    params?: Record<string, string | number | boolean>,
  ) =>
    instance.get<{ data: PersonalBestRecordItem[] }>(
      `/api/v1/mobile/records/history/user/${userId}`,
      { params },
    ),
};

// ─── Calendar Endpoints ──────────────────────────────────────────────────────

export interface CalendarWorkoutType {
  id: number;
  name: string;
  english_name: string;
  icon: string;
}

export interface CalendarWorkout {
  annual_records?: WorkoutAnnualRecord[];
  annual_records_count?: number;
  id: number;
  workout_type: CalendarWorkoutType;
  title: string;
  workout_day: string;
  total_time: number | null;
  total_time_planned: string | null;
  tss_actual: number | null;
  training_load?: TrainingLoadMeta | null;
  running_metrics?: RunningMetrics | null;
  source?: string | null;
  fit_sport?: number | null;
  tss_planned: string | null;
  if_planned: string | null;
  description: string | null;
  distance: number | null;
  distance_planned: string | null;
  calories: string | null;
  order_on_day: number | null;
  is_created_by_athlete: boolean;
  structure: unknown | null;
  comments: number;
}

export interface CalendarNote {
  id: number;
  title?: string;
  description?: string | null;
  note_date?: string;
  content: string;
  date: string;
}

export interface CalendarEvent {
  id: number;
  name: string;
  event_date: string;
  description?: string | null;
  sport_type_id?: unknown;
  atp_priority?: number | null;
  race_type_duration?: string | null;
}

export interface CalendarTodayMetrics {
  id: number;
  ctl: string;
  atl: string;
  tsb: string;
  if_actual: number;
  tss_actual: number;
  calculation_date: string;
}

export interface CalendarHealthDocument {
  id: number;
  user_id?: number;
  original_filename: string;
  test_type?: string | null;
  mime_type?: string | null;
  status: string;
  unique_code?: string | null;
  saved_metrics_count: number;
  has_file: boolean;
  calendar_date?: string | null;
  error_message?: string | null;
}

export interface CalendarDayEntry {
  day: string;
  workouts: CalendarWorkout[];
  events: CalendarEvent[];
  notes: CalendarNote[];
  userMetrics: unknown[];
  healthDocuments?: CalendarHealthDocument[];
  today: CalendarTodayMetrics | null;
}

export const calendar = {
  /** GET /api/v1/mobile/calendar/user/{userId}?start_date=YYYY-MM-DD&end_date=YYYY-MM-DD */
  getCalendarData: (userId: number, startDate: string, endDate: string) =>
    instance.get<{ status: string; message: string; data: CalendarDayEntry[] }>(
      `/api/v1/mobile/calendar/user/${userId}`,
      { params: { start_date: startDate, end_date: endDate } }
    ),

  /** GET /api/v1/mobile/workout/search-completed/user/{userId} — full-history completed workout search */
  searchCompletedWorkouts: (userId: number, q: string, excludeId?: number) =>
    instance.get<{ data: { workouts: CalendarWorkout[] } }>(
      `/api/v1/mobile/workout/search-completed/user/${userId}`,
      { params: { q, exclude_id: excludeId } },
    ),
};

// ─── Form creation types ─────────────────────────────────────────────────────

export interface EventSportType {
  id: number;
  name: string;
  persian_name: string;
  child: EventSportType[];
}

export interface UserMetricItem {
  id: number;
  fa_name: string;
  en_name: string;
}

export interface UserMetric {
  metric_type_id: number;
  name: string;
  unit?: string;
  value?: string | number | null;
  unique_code?: string | null;
  user_metric_data_id?: number | null;
  data_type?: string;
  metricTypeItems?: UserMetricItem[];
  metric_type_items?: UserMetricItem[];
}

export const forms = {
  /** POST /api/v1/mobile/workout/create-workout */
  createWorkout: (
    userId: number,
    workout: Record<string, unknown>,
    comments?: Array<Record<string, unknown>> | null,
  ) =>
    instance.post('/api/v1/mobile/workout/create-workout', {
      user_id: userId,
      data: { workout, comments: comments ?? null },
    }),

  /** Fetch event sports and their children using database IDs. */
  getEventTypes: () =>
    instance.get<{ data: { sportTypes: EventSportType[] } }>('/api/v1/mobile/event/sport-types'),

  /** GET /api/v1/mobile/event/user/{userId}?start_date=&end_date= */
  getUserEventsInRange: (userId: number, startDate: string, endDate: string) =>
    instance.get<{ data: CalendarEvent[] }>(`/api/v1/mobile/event/user/${userId}`, {
      params: { start_date: startDate, end_date: endDate },
    }),

  /** POST /api/v1/mobile/event/add-event */
  createEvent: (userId: number, data: Record<string, unknown>) =>
    instance.post('/api/v1/mobile/event/add-event', { user_id: userId, data }),

  /** GET /api/v1/mobile/event/get-event/{eventId} (coach) or athlete/get-event (self) */
  getEvent: (eventId: number, options?: { asAthlete?: boolean }) =>
    instance.get<{ data: { event: CalendarEvent } }>(
      options?.asAthlete
        ? `/api/v1/mobile/athlete/get-event/${eventId}`
        : `/api/v1/mobile/event/get-event/${eventId}`,
    ),

  /** PATCH /api/v1/mobile/event/{eventId} */
  updateEvent: (eventId: number, data: Record<string, unknown>) =>
    instance.patch(`/api/v1/mobile/event/${eventId}`, { data }),

  /** DELETE /api/v1/mobile/event/{eventId} */
  deleteEvent: (eventId: number) =>
    instance.delete(`/api/v1/mobile/event/${eventId}`),

  /** GET /api/v1/mobile/calendar/get-user-metrics/{userId} */
  getUserMetrics: (userId: number) =>
    instance.get<{ data: { metrics: UserMetric[] } }>(
      `/api/v1/mobile/calendar/get-user-metrics/${userId}`
    ),

  /** GET /api/v1/mobile/calendar/get-metric-by-unique-code — edit existing day entry */
  getMetricByUniqueCode: (userId: number, uniqueCode: string) =>
    instance.get<{ data: { metrics: UserMetric[] } }>(
      '/api/v1/mobile/calendar/get-metric-by-unique-code',
      { params: { unique_code: uniqueCode, user_id: userId } },
    ),

  /** POST /api/v1/mobile/calendar/add-metric-data */
  createMetric: (userId: number, date: string, metrics: Array<{ metric_type_id: number; value: string | number }>) =>
    instance.post('/api/v1/mobile/calendar/add-metric-data', {
      user_id: userId,
      date,
      data: metrics,
    }),

  /** PATCH /api/v1/mobile/calendar/update-metric-data */
  updateMetric: (
    userId: number,
    uniqueCode: string,
    metrics: Array<{ metric_type_id: number; value: string; user_metric_data_id?: number | null }>,
  ) =>
    instance.patch('/api/v1/mobile/calendar/update-metric-data', {
      user_id: userId,
      unique_code: uniqueCode,
      data: metrics,
    }),

  /** POST /api/v1/mobile/note */
  createNote: (userId: number, noteDate: string, title: string, description: string) =>
    instance.post('/api/v1/mobile/note', {
      user_id: userId,
      note_date: noteDate,
      title,
      description,
      is_visible_to_athlete: true,
    }),

  /** DELETE /api/v1/mobile/note/{noteId} */
  deleteNote: (noteId: number) =>
    instance.delete(`/api/v1/mobile/note/${noteId}`),

  /** DELETE /api/v1/mobile/calendar/delete-metrics */
  deleteMetric: (uniqueCode: string) =>
    instance.delete('/api/v1/mobile/calendar/delete-metrics', {
      data: { unique_code: uniqueCode },
    }),
};

// ─── Home / Readiness Endpoints ─────────────────────────────────────────────

export interface ReadinessFactor {
  available: boolean;
  value: number | null;
  score: number;
  source?: 'training_metrics' | 'user_metrics_data' | 'current_only' | 'personal_28d_median' | string;
  kind?: 'hours' | 'quality' | 'combined' | null;
  hours?: number | null;
  quality?: number | null;
  baseline?: number | null;
  delta_percent?: number | null;
  baseline_samples?: number;
  interpretation?: 'fatigued' | 'balanced' | 'detraining_risk' | string;
}

export interface ReadinessToday {
  score: number;
  level: 'optimal' | 'good' | 'fair' | 'poor';
  label: string;
  methodology_version?: 'readiness_v2' | string;
  coverage?: number;
  confidence?: 'low' | 'medium' | 'high';
  provenance?: {
    date: string;
    inputs: string[];
    hrv_baseline_window: {
      days: number;
      from: string | null;
      to: string | null;
      minimum_samples: number;
    };
  };
  factors: {
    tsb: ReadinessFactor;
    hrv: ReadinessFactor;
    sleep: ReadinessFactor;
    fatigue: ReadinessFactor;
    soreness: ReadinessFactor;
  };
}

export interface ReadinessHistory {
  date: string;
  score: number;
  coverage?: number;
}

export interface TodayTrends {
  ctl: number | null;
  atl: number | null;
  tsb: number | null;
  method?: TrainingLoadMethod | null;
  coverage?: number | null;
  classified_sessions?: number;
  total_sessions?: number;
}

export interface HomeEvent {
  id: number;
  name?: string;
  title?: string;
  event_date: string;
  description?: string;
}

export interface WeeklySummaryData {
  'weekly-summary': {
    totalTime: number;
    totalDistance: number;
    totalTSS: number;
    totalTimePlanned: number;
    totalDistancePlanned: number;
    totalTSSPlanned: number;
    trainingLoadProfile?: {
      method: TrainingLoadMethod | null;
      coverage: number | null;
      classified_sessions: number;
      total_sessions: number;
    };
  };
  daily: Array<{
    date: string;
    summary: {
      total_time: { actual: number; planned: number };
      total_distance: { actual: number; planned: number };
      total_tss: { actual: number; planned: number };
    };
  }>;
}

export interface TrendDailyEntry {
  date: string;
  summary: {
    total_time: { actual: number; planned: number };
    total_distance: { actual: number; planned: number };
    total_tss: { actual: number; planned: number };
  };
}

export interface WhoopHealthMetric {
  value: number | string;
  recorded_at: string | null;
}

export interface WhoopHealthSummary {
  provider: 'whoop';
  connected: true;
  latest_at: string | null;
  metrics: {
    recovery_score: WhoopHealthMetric | null;
    resting_heart_rate: WhoopHealthMetric | null;
    spo2: WhoopHealthMetric | null;
    skin_temperature: WhoopHealthMetric | null;
    respiratory_rate: WhoopHealthMetric | null;
    strain: WhoopHealthMetric | null;
  };
}

export interface HomeData {
  readiness: { today: ReadinessToday; history: ReadinessHistory[] } | null;
  today_trends: TodayTrends | null;
  summery: { total_workouts: number; total_duration: number; total_distance: number }[] | null;
  weekly_metrics: {
    current_week: { total_tss: number; total_duration: number; total_distance: number };
    previous_week: { total_tss: number; total_duration: number; total_distance: number };
  } | null;
  current_week_summary?: WeeklySummaryData | null;
  /** Rolling 7-day daily totals for home trend tabs (time / distance / TSS). */
  trend_daily?: TrendDailyEntry[] | null;
  /** Present only while the athlete has an active WHOOP connection. */
  whoop_health?: WhoopHealthSummary | null;
  events?: HomeEvent[];
  up_coming_events?: HomeEvent[];
  workouts?: BriefWorkout[];
}

export interface BriefWorkout {
  id: number;
  title?: string;
  workout_day?: string;
  total_time?: number | null;
  total_time_planned?: number | null;
  tss_actual?: number | null;
  training_load?: TrainingLoadMeta | null;
  running_metrics?: RunningMetrics | null;
  source?: string | null;
  fit_sport?: number | null;
  tss_planned?: number | null;
  distance?: number | null;
  is_completed?: boolean;
  workout_type?: { name?: string; english_name?: string };
  workout_subtype?: { name?: string };
}

export const home = {
  /** GET /api/v1/mobile/athlete/home — optional user_id when coach views an athlete */
  getHomeData: (userId?: number | null) =>
    instance.get<{ data: HomeData }>('/api/v1/mobile/athlete/home', {
      params: userId ? { user_id: userId } : undefined,
    }),
};

// ─── Calendar Actions ────────────────────────────────────────────────────────

export const calendarActions = {
  /** POST /copy-paste-workout/user/{user} — duplicate a workout to another day */
  copyPasteWorkout: (userId: number, workoutId: number, selectedDay: string) =>
    instance.post(`/api/v1/mobile/calendar/copy-paste-workout/user/${userId}`, {
      workout_id: workoutId,
      selected_day: selectedDay,
    }),

  /** POST /cut-paste-workout/user/{user} — move (drag & drop) a workout to another day */
  cutPasteWorkout: (userId: number, workoutId: number, selectedDay: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-paste-workout/user/${userId}`, {
      workout_id: workoutId,
      selected_day: selectedDay,
      user_id: userId,       // controller reads $request->user_id to set workout owner
    }),

  /** POST /cut-paste-metrics/user/{user} — move a metric group (unique_code) to another day */
  cutPasteMetrics: (userId: number, uniqueCode: string, metricDate: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-paste-metrics/user/${userId}`, {
      unique_code: uniqueCode,
      metric_date: metricDate,
    }),

  cutPasteHealthDocument: (userId: number, healthDocumentId: number, testDate: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-paste-health-document/user/${userId}`, {
      health_document_id: healthDocumentId,
      test_date: testDate,
    }),

  cutPasteEvent: (userId: number, eventId: number, eventDate: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-paste-event/user/${userId}`, {
      event_id: eventId,
      event_date: eventDate,
    }),

  cutPasteNote: (userId: number, noteId: number, noteDate: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-paste-note/user/${userId}`, {
      note_id: noteId,
      note_date: noteDate,
    }),

  /** DELETE /api/v1/mobile/workout/{workoutId} — permanently delete a workout */
  deleteWorkout: (_userId: number, workoutId: number) =>
    instance.delete(`/api/v1/mobile/workout/${workoutId}`),

  /** POST /copy-by-date/user/{user} — copy all items from one day to another */
  copyByDate: (userId: number, fromDate: string, toDate: string) =>
    instance.post(`/api/v1/mobile/calendar/copy-by-date/user/${userId}`, {
      from_date: fromDate,
      to_date: toDate,
    }),

  /** POST /cut-by-date/user/{user} — move all items from one day to another */
  cutByDate: (userId: number, fromDate: string, toDate: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-by-date/user/${userId}`, {
      from_week_date: fromDate,   // controller reads $request->from_week_date
      to_week_date: toDate,       // controller reads $request->to_week_date
    }),

  /** POST /copy-by-date-range/src/{src}/dst/{dst} — copy a full week range to another week */
  copyByDateRange: (userId: number, fromWeekDate: string, toWeekDate: string) =>
    instance.post(
      `/api/v1/mobile/calendar/copy-by-date-range/src/${userId}/dst/${userId}`,
      { from_week_date: fromWeekDate, to_week_date: toWeekDate }
    ),

  /** POST /cut-by-date-range/user/{user} — move a full week range to another week */
  cutByDateRange: (userId: number, fromWeekDate: string, toWeekDate: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-by-date-range/user/${userId}`, {
      from_week_date: fromWeekDate,
      to_week_date: toWeekDate,
    }),

  /** POST /delete-by-date-range/user/{user} — delete all items in a date range */
  deleteByDateRange: (userId: number, startDate: string, endDate: string) =>
    instance.post(`/api/v1/mobile/calendar/delete-by-date-range/user/${userId}`, {
      date_range: [startDate, endDate],  // controller passes to whereBetween('workout_day', $dateRange)
    }),
};

/** Cross-athlete calendar ops — production exposes these under /api/v1/mobile/calendar (not /api/v1/calendar). */
export const calendarActionsCross = {
  copyWorkoutToUser: (destUserId: number, workoutId: number, selectedDay: string) =>
    instance.post(`/api/v1/mobile/calendar/copy-paste-workout/user/${destUserId}`, {
      workout_id: workoutId,
      selected_day: selectedDay,
    }),

  moveWorkoutToUser: (destUserId: number, workoutId: number, selectedDay: string) =>
    instance.post(`/api/v1/mobile/calendar/cut-paste-workout/user/${destUserId}`, {
      workout_id: workoutId,
      selected_day: selectedDay,
      user_id: destUserId,
    }),

  copyDayToUser: (sourceUserId: number, destUserId: number, fromDate: string, toDate: string) =>
    instance.post(
      `/api/v1/mobile/calendar/copy-by-date/src/${sourceUserId}/dst/${destUserId}`,
      { from_date: fromDate, to_date: toDate },
    ),

  copyWeekToUser: (
    sourceUserId: number,
    destUserId: number,
    fromWeekDate: string,
    toWeekDate: string,
  ) =>
    instance.post(
      `/api/v1/mobile/calendar/copy-by-date-range/src/${sourceUserId}/dst/${destUserId}`,
      { from_week_date: fromWeekDate, to_week_date: toWeekDate },
    ),

  deleteWorkout: (userId: number, workoutId: number) =>
    instance.post(`/api/v1/mobile/calendar/delete-workout/user/${userId}`, {
      workout_id: workoutId,
    }),
};

// ─── Chart Endpoints ─────────────────────────────────────────────────────────

export interface PmcDataPoint {
  date: string;
  ctl: number | string;
  atl: number | string;
  tsb: number | string;
  tss?: number | string | null;
}

export interface FitnessSummaryItem {
  label?: string;
  sport_type?: string;
  name?: string;
  pct?: number | string;
  percentage?: number | string;
  hours?: number | string;
  total_time?: number | string;
  color?: string;
}

export interface WeeklyBarPoint {
  week?: string;
  date?: string;
  label?: string;
  value?: number | string;
  total?: number | string;
  duration?: number | string;
  distance?: number | string;
  calories?: number | string;
}

export interface HrZoneWeekPoint {
  week?: string;
  date?: string;
  z1?: number | string;
  z2?: number | string;
  z3?: number | string;
  z4?: number | string;
  z5?: number | string;
  zone_1?: number | string;
  zone_2?: number | string;
  zone_3?: number | string;
  zone_4?: number | string;
  zone_5?: number | string;
}

export const charts = {
  /** GET /api/v1/mobile/chart/performance-management/user/{user} */
  performanceManagement: (userId: number, params?: Record<string, unknown>) =>
    instance.get<{ data: PmcDataPoint[] | { data: PmcDataPoint[] } }>(
      `/api/v1/mobile/chart/performance-management/user/${userId}`,
      { params }
    ),

  /** GET /api/v1/mobile/chart/fitness-summary/user/{user} */
  fitnessSummary: (userId: number, params?: Record<string, unknown>) =>
    instance.get<{ data: FitnessSummaryItem[] | { data: FitnessSummaryItem[] } }>(
      `/api/v1/mobile/chart/fitness-summary/user/${userId}`,
      { params }
    ),

  /** GET /api/v1/mobile/chart/duration-by-week-or-day/user/{user} */
  durationByWeekOrDay: (userId: number, params?: Record<string, unknown>) =>
    instance.get<{ data: WeeklyBarPoint[] | { data: WeeklyBarPoint[] } }>(
      `/api/v1/mobile/chart/duration-by-week-or-day/user/${userId}`,
      { params }
    ),

  /** GET /api/v1/mobile/chart/distance-by-week-or-day/user/{user} */
  distanceByWeekOrDay: (userId: number, params?: Record<string, unknown>) =>
    instance.get<{ data: WeeklyBarPoint[] | { data: WeeklyBarPoint[] } }>(
      `/api/v1/mobile/chart/distance-by-week-or-day/user/${userId}`,
      { params }
    ),

  /** GET /api/v1/mobile/chart/calories-by-time-period/user/{user} */
  caloriesByTimePeriod: (userId: number, params?: Record<string, unknown>) =>
    instance.get<{ data: WeeklyBarPoint[] | { data: WeeklyBarPoint[] } }>(
      `/api/v1/mobile/chart/calories-by-time-period/user/${userId}`,
      { params }
    ),

  /** GET /api/v1/mobile/chart/time-in-zones-by-week/user/{user} */
  timeInZonesByWeek: (userId: number, params?: Record<string, unknown>) =>
    instance.get<{ data: HrZoneWeekPoint[] | { data: HrZoneWeekPoint[] } }>(
      `/api/v1/mobile/chart/time-in-zones-by-week/user/${userId}`,
      { params }
    ),

  /** GET /api/v1/mobile/chart/tss-by-week-or-day/user/{user} */
  tssByWeekOrDay: (userId: number, params?: Record<string, unknown>) =>
    instance.get<{ data: WeeklyBarPoint[] | { data: WeeklyBarPoint[] } }>(
      `/api/v1/mobile/chart/tss-by-week-or-day/user/${userId}`,
      { params }
    ),
};

// ─── Chart Manager Endpoints ─────────────────────────────────────────────────

export interface AvailableChart {
  id: number;
  name: string;
  chart_type: string;
  default_setting?: Record<string, unknown>;
}

export interface UserChartEntry {
  data: unknown;
  setting: Record<string, unknown>;
  chart_type: string;
  chart_id: number;
  priority: number;
}

export interface MetricTypeOption {
  id: number;
  name: string;
}

export const metricsApi = {
  listTypes: () =>
    instance.get<{ data: { metrics: MetricTypeOption[] } }>('/api/v1/mobile/metric/'),
};

export const chartManager = {
  getRunningProfile: (userId: number, params?: { period?: RunningPeriod; start_date?: string; end_date?: string; benchmark_version?: string }, signal?: AbortSignal) =>
    instance.get<{ data: RunningProfile }>(`/api/v1/mobile/athletes/${userId}/running-profile`, { params, signal }),
  getAvailable: () =>
    instance.get<{ data: { charts: AvailableChart[] } }>('/api/v1/mobile/chart/default-chart'),
  getUserCharts: (userId: number, params?: { start_date?: string; end_date?: string; range?: number }) =>
    instance.get<{ data: UserChartEntry[] }>(`/api/v1/mobile/chart/all-charts/user/${userId}`, { params }),
  addChart: (chartId: number, userId: number, setting?: Record<string, unknown>) =>
    instance.post(`/api/v1/mobile/chart/create-user-chart/${chartId}/user/${userId}`, setting ? {setting} : undefined),
  removeChart: (userChartId: number) =>
    instance.delete(`/api/v1/mobile/chart/user-charts/${userChartId}`),
  updatePriority: (priorities: Array<{ id: number; index: number }>) =>
    instance.patch('/api/v1/mobile/chart/chart-priority', { priorities }),
  updateChartSettings: (userChartId: number, setting: Record<string, unknown>) =>
    instance.patch(`/api/v1/mobile/chart/chart-setting/${userChartId}`, { setting }),
  recalculateTrainingMetrics: (
    userChartId: number,
    userId: number,
    body: {
      start_date: string;
      end_date: string;
      full_history?: boolean;
      ctl_period?: number;
      atl_period?: number;
      initial_ctl?: number;
      initial_atl?: number;
    },
  ) =>
    instance.post(
      `/api/v1/mobile/chart/recalculate-training-metrics/${userChartId}/user/${userId}`,
      body,
    ),
};

// ─── Library Endpoints ───────────────────────────────────────────────────────

export interface LibraryExercise {
  id: number;
  title: string;
  tss_planned: number | null;
  distance_planned: number | null;
  total_time_planned: number | null;
  workout_type: { id: number; name: string; english_name: string; icon?: string } | null;
  structure: {
    structure: any[];
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
  } | null;
}

export interface LibraryItem {
  id: number;
  lib_name: string;
  exercises: LibraryExercise[];
}

export const library = {
  /** GET /api/v1/mobile/athlete/library/all-libraries */
  getAll: () =>
    instance.get<{ data: LibraryItem[] }>('/api/v1/mobile/athlete/library/all-libraries'),

  /** POST /api/v1/mobile/library/create */
  create: (name: string) =>
    instance.post('/api/v1/mobile/library/create', { lib_name: name }),

  /** PUT /api/v1/mobile/library/{id} */
  update: (id: number, name: string) =>
    instance.put(`/api/v1/mobile/library/${id}`, { lib_name: name }),

  /** DELETE /api/v1/mobile/library/{id} */
  delete: (id: number) =>
    instance.delete(`/api/v1/mobile/library/${id}`),

  /** POST /api/v1/mobile/library/{libraryId}/exercise/create */
  addExercise: (libraryId: number, data: {
    title?: string;
    description?: string | null;
    workout_type_id?: number;
    total_time_planned?: number | null;
    tss_planned?: number | null;
    if_planned?: number | null;
    distance_planned?: number | null;
    calories_planned?: number | null;
    structure?: unknown;
  }) =>
    instance.post(`/api/v1/mobile/library/${libraryId}/exercise/create`, {
      data: { exercise: data },
    }),

  /** PUT /api/v1/mobile/library/exercise/{id} */
  updateExercise: (id: number, data: {
    data: {
      exercise: {
        title?: string;
        description?: string | null;
        workout_type_id?: number;
        total_time_planned?: number | null;
        tss_planned?: number | null;
        if_planned?: number | null;
        distance_planned?: number | null;
        calories_planned?: number | null;
        structure?: unknown;
      };
      tags?: string[];
    };
  }) =>
    instance.put(`/api/v1/mobile/library/exercise/${id}`, data),

  /** DELETE /api/v1/mobile/library/exercise/{id} */
  deleteExercise: (id: number) =>
    instance.delete(`/api/v1/mobile/library/exercise/${id}`),

  /** POST /api/v1/mobile/library/exercise/{id}/add-to-workout */
  addExerciseToCalendar: (exerciseId: number, userId: number, date: string) =>
    instance.post(`/api/v1/mobile/library/exercise/${exerciseId}/add-to-workout`, {
      user_id: userId,
      selected_day: date,
    }),
};

// ─── Coach ↔ Athlete Requests ────────────────────────────────────────────────

export interface AthleteLookup {
  id: number;
  full_name: string;
  profile_image?: string;
  mobile?: string;
  is_coached?: boolean;
}

export interface CoachLookup {
  id: number;
  full_name: string;
  profile_image?: string;
  coach_logo?: string;
  mobile?: string;
}

export interface CoachAthleteRequestItem {
  id: number;
  status: 'pending' | 'accepted' | 'declined';
  initiated_by?: 'coach' | 'athlete';
  coach?: {
    id: number;
    full_name: string;
    profile_image?: string;
    coach_logo?: string;
  };
  athlete?: {
    id: number;
    full_name: string;
    profile_image?: string;
    mobile?: string;
  };
  created_at?: string;
}

export interface AppNotification {
  details?: { scope?: string; count?: number; year?: number; section?: string; records?: unknown[] } | null;
  workout_kind?: string | null;
  id: number;
  description?: string;
  notifiable_type?: string;
  notifiable?: CoachAthleteRequestItem | Record<string, unknown>;
  is_read?: boolean;
  created_at?: string;
  user_id?: { full_name?: string };
  actor?: { id?: number; full_name?: string; profile_image?: string; avatar?: string } | null;
}

export const coachAthlete = {
  /** POST /api/v1/mobile/account/find-athlete-by-mobile */
  findByMobile: (mobile: string) =>
    instance.post<{ data: { athlete: AthleteLookup } }>(
      '/api/v1/mobile/account/find-athlete-by-mobile',
      { mobile },
    ),

  /** POST /api/v1/mobile/account/send-coach-request */
  sendRequest: (athleteId: number, replaceCoach = false) =>
    instance.post<{ data: { request: CoachAthleteRequestItem } }>(
      '/api/v1/mobile/account/send-coach-request',
      { athlete_id: athleteId, replace_coach: replaceCoach },
    ),

  findCoachByMobile: (mobile: string) =>
    instance.post<{ data: { coach: CoachLookup } }>(
      '/api/v1/mobile/athlete/find-coach-by-mobile',
      { mobile },
    ),

  sendAthleteRequest: (coachId: number, replaceCoach = false) =>
    instance.post<{ data: { request: CoachAthleteRequestItem } }>(
      '/api/v1/mobile/athlete/send-coach-request',
      { coach_id: coachId, replace_coach: replaceCoach },
    ),

  /** POST /api/v1/mobile/account/remove-athlete/{athlete} */
  remove: (athleteId: number) =>
    instance.post(`/api/v1/mobile/account/remove-athlete/${athleteId}`),

  /** GET /api/v1/mobile/account/search-assistant-coach */
  searchAssistantCoachByMobile: (mobile: string) =>
    instance.get<{ data: { coach: AssistantCoachLookup } }>(
      '/api/v1/mobile/account/search-assistant-coach',
      { params: { mobile } },
    ),

  /** GET /api/v1/mobile/account/assistant-assignments/athlete/{athleteId} */
  getAthleteAssistantCoaches: (athleteId: number) =>
    instance.get<{ data: { assignments: AssistantAssignment[] } }>(
      `/api/v1/mobile/account/assistant-assignments/athlete/${athleteId}`,
    ),

  /** POST /api/v1/mobile/account/assistant-assignments */
  addAssistantCoach: (payload: {
    athlete_id: number;
    assistant_coach_id: number;
    permissions?: Record<string, boolean>;
  }) =>
    instance.post<{ data: { assignment: AssistantAssignment } }>(
      '/api/v1/mobile/account/assistant-assignments',
      payload,
    ),

  /** DELETE /api/v1/mobile/account/assistant-assignments/{id} */
  removeAssistantCoach: (assignmentId: number) =>
    instance.delete(`/api/v1/mobile/account/assistant-assignments/${assignmentId}`),
};

export interface AssistantCoachLookup {
  id: number;
  full_name: string;
  mobile?: string;
  email?: string;
}

export interface AssistantAssignment {
  id: number;
  coach_id: number;
  athlete_id: number;
  status: string;
  permissions: Record<string, boolean>;
  coach?: AssistantCoachLookup;
}

export const notificationsApi = {
  /** GET /api/v1/mobile/athlete/notifications (athlete) or /notifications (coach) */
  list: (asAthlete: boolean, page = 1, countOnly = false) =>
    instance.get<{ data: { notifications: Record<string, AppNotification[]>; unread_count?: number; pagination?: { current_page: number; next_page: number | null; per_page: number; total: number } } }>(
      asAthlete ? '/api/v1/mobile/athlete/notifications' : '/api/v1/mobile/notifications',
      { params: countOnly ? { count_only: 1 } : { page }, timeout: countOnly ? 30_000 : 60_000 },
    ),

  acceptCoachRequest: (requestId: number) =>
    instance.post(`/api/v1/mobile/athlete/notifications/coach-request/${requestId}/accept`),

  declineCoachRequest: (requestId: number) =>
    instance.post(`/api/v1/mobile/athlete/notifications/coach-request/${requestId}/decline`),

  acceptCoachRequestAsCoach: (requestId: number) =>
    instance.post(`/api/v1/mobile/account/notifications/coach-request/${requestId}/accept`),

  declineCoachRequestAsCoach: (requestId: number) =>
    instance.post(`/api/v1/mobile/account/notifications/coach-request/${requestId}/decline`),

  markAllRead: (asAthlete: boolean) =>
    instance.put(
      asAthlete
        ? '/api/v1/mobile/athlete/notifications/mark-all-as-read'
        : '/api/v1/mobile/notifications/mark-all-as-read',
    ),

  markAsRead: (asAthlete: boolean, notificationIds: number[]) =>
    instance.put(
      asAthlete
        ? '/api/v1/mobile/athlete/notifications/mark-as-read'
        : '/api/v1/mobile/notifications/mark-as-read',
      { notification_ids: notificationIds },
    ),
};

export const deviceTokensApi = {
  register: (payload: { token: string; platform?: 'web' | 'android' | 'ios'; device_id?: string }) =>
    instance.post<{ data: { registered: boolean } }>('/api/v1/mobile/device-tokens', {
      platform: 'web',
      ...payload,
    }),

  unregister: (payload: { token: string }) =>
    instance.delete<{ data: { deleted: boolean } }>('/api/v1/mobile/device-tokens', {
      data: payload,
    }),
};

export interface WorkoutComparisonPayload {
  name: string;
  tags?: string[];
  notes?: string;
  workout_a_id: number;
  workout_b_id?: number | null;
  segment_a?: { start_sec: number; end_sec: number } | null;
  segment_b?: { start_sec: number; end_sec: number } | null;
  config?: Record<string, unknown>;
  sparkline_a?: number[];
  sparkline_b?: number[];
}

export const comparisons = {
  list: (userId: number, params?: { search?: string; tag?: string; page?: number }) =>
    instance.get(`/api/v1/mobile/comparisons/user/${userId}`, { params }),

  create: (userId: number, payload: WorkoutComparisonPayload) =>
    instance.post(`/api/v1/mobile/comparisons/user/${userId}`, payload),

  get: (id: number) => instance.get(`/api/v1/mobile/comparisons/${id}`),

  update: (id: number, payload: Partial<WorkoutComparisonPayload>) =>
    instance.put(`/api/v1/mobile/comparisons/${id}`, payload),

  delete: (id: number) => instance.delete(`/api/v1/mobile/comparisons/${id}`),

  enableShare: (id: number) => instance.post(`/api/v1/mobile/comparisons/${id}/share`),

  revokeShare: (id: number) => instance.delete(`/api/v1/mobile/comparisons/${id}/share`),

  getShared: (token: string) => instance.get(`/api/v1/mobile/comparisons/shared/${token}`),
};

export interface HealthDocumentSavedMetric {
  metric_type_id: number;
  name: string;
  value: string;
  unit?: string | null;
  key?: string;
}

export interface HealthDocumentAnalysisData {
  id: number;
  user_id: number;
  status: 'pending' | 'processing' | 'completed' | 'partial' | 'failed';
  test_type?: string | null;
  test_date?: string | null;
  original_filename?: string | null;
  saved_metrics: HealthDocumentSavedMetric[];
  skipped_metrics: Array<Record<string, unknown>>;
  warnings: string[];
  unique_code?: string | null;
  provider?: string | null;
  model?: string | null;
  latency_ms?: number | null;
  error_message?: string | null;
  created_at?: string | null;
}

export const healthDocuments = {
  analyze: (
    file: File,
    options?: {
      user_id?: number;
      test_date_hint?: string;
      note?: string;
      locale?: string;
    },
  ) => {
    const form = new FormData();
    form.append('file', file);
    if (options?.user_id != null) form.append('user_id', String(options.user_id));
    if (options?.test_date_hint) form.append('test_date_hint', options.test_date_hint);
    if (options?.note) form.append('note', options.note);
    if (options?.locale) form.append('locale', options.locale);

    return instance.post<{ data: HealthDocumentAnalysisData }>(
      '/api/v1/mobile/health-documents/analyze',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  getStatus: (id: number) =>
    instance.get<{ data: HealthDocumentAnalysisData }>(`/api/v1/mobile/health-documents/${id}`),

  downloadFile: async (id: number): Promise<Blob> => {
    const res = await instance.get(`/api/v1/mobile/health-documents/${id}/file`, {
      responseType: 'blob',
    });
    return res.data as Blob;
  },

  destroy: (id: number) =>
    instance.delete(`/api/v1/mobile/health-documents/${id}`),
};

/** VO2 Bridge UI locale bundles (public — no auth). */
export const bridgeLocales = {
  list: () => instance.get('/api/v1/mobile/bridge/locales'),
  fetch: (locale: string) => instance.get(`/api/v1/mobile/bridge/locales/${locale}`),
};


/** Coach-only agent workspace. Authorization is also enforced by every server endpoint. */
export const coachAssistant = {
  async bootstrap(signal?: AbortSignal): Promise<import('../components/ai/assistantTypes').AssistantBootstrap> {
    const { data } = await instance.get('/api/v1/mobile/ai/assistant', { signal });
    return data.data;
  },
  async conversation(id: string): Promise<import('../components/ai/assistantTypes').AssistantConversation> {
    const { data } = await instance.get(`/api/v1/mobile/ai/assistant/conversations/${encodeURIComponent(id)}`);
    return data.data;
  },
  async chat(payload: { message: string; request_id: string; conversation_id?: string; athlete_ids: number[]; days: number; locale: string }, signal?: AbortSignal): Promise<import('../components/ai/assistantTypes').AssistantConversation> {
    const { data } = await instance.post('/api/v1/mobile/ai/assistant/chat', payload, { signal, timeout: 180_000 });
    return data.data;
  },
};
